import React, { useCallback, useEffect, useState } from 'react';
import {
  KeyIcon,
  ClipboardDocumentIcon,
  TrashIcon,
  PencilSquareIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { Check, ChevronDown, Circle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../connections/api';
import routes_api from '../../../connections/routes_api';

const ScopeChips = ({ availableScopes, selectedScopes, onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {availableScopes.map(({ scope, label }) => {
      const selected = selectedScopes.includes(scope);
      return (
        <label
          key={scope}
          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
            selected
              ? 'border-green-200 bg-green-100 text-green-800'
              : 'border-gray-200 bg-gray-100 text-gray-500'
          }`}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={selected}
            onChange={() => onToggle(scope)}
          />
          {selected ? (
            <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
          ) : (
            <Circle className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
          )}
          {label}
        </label>
      );
    })}
  </div>
);

const McpKeysSection = () => {
  const [keys, setKeys] = useState([]);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [newKeyPlaintext, setNewKeyPlaintext] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editScopes, setEditScopes] = useState([]);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [keysRes, scopesRes] = await Promise.all([
        api.get(routes_api.accounts_mcp_keys),
        api.get(routes_api.accounts_mcp_scopes),
      ]);
      setKeys(keysRes.data || []);
      const scopes = scopesRes.data || [];
      setAvailableScopes(scopes);
      setSelectedScopes((prev) =>
        prev.length ? prev : scopes.map((s) => s.scope)
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to load MCP keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleIn = (setter) => (scope) => {
    setter((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleCreate = async () => {
    if (!selectedScopes.length) {
      toast.error('Select at least one scope.');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post(routes_api.accounts_mcp_keys, {
        name: name.trim() || 'MCP key',
        scopes: selectedScopes,
      });
      setNewKeyPlaintext(res.data.api_key);
      setName('');
      setFormOpen(false);
      toast.success('MCP key created. Copy it now.');
      await load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to create MCP key.');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (k) => {
    setEditingId(k.id);
    setEditName(k.name || '');
    setEditScopes([...(k.scopes || [])]);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditScopes([]);
  };

  const handleSaveEdit = async () => {
    if (!editScopes.length) {
      toast.error('Select at least one scope.');
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`${routes_api.accounts_mcp_keys}${editingId}/`, {
        name: editName.trim() || 'MCP key',
        scopes: editScopes,
      });
      toast.success('Key updated.');
      cancelEdit();
      await load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update MCP key.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied.');
    } catch {
      toast.error('Could not copy.');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this MCP key? Engines using it will stop working.')) {
      return;
    }
    try {
      await api.delete(`${routes_api.accounts_mcp_keys}${id}/`);
      toast.success('Key revoked.');
      if (editingId === id) cancelEdit();
      await load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to revoke key.');
    }
  };

  if (loading) {
    return (
      <div className="mt-8 border-t pt-6">
        <div className="flex justify-center py-4">
          <div className="border-4 border-gray-300 border-t-gray-700 rounded-full w-8 h-8 animate-spin" />
        </div>
      </div>
    );
  }

  const visibleKeys = showRevoked ? keys : keys.filter((k) => k.is_active);
  const revokedCount = keys.filter((k) => !k.is_active).length;

  return (
    <div className="mt-8 border-t pt-6">
      <h3 className="text-md font-semibold mb-2 flex items-center gap-2">
        <KeyIcon className="w-5 h-5" />
        MCP / Agent API keys
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Create a key to let an assistant access the modules you choose. You can rename it
        or change that access later, without replacing the secret.
      </p>

      {newKeyPlaintext && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-medium text-amber-900 mb-2">
            New key (shown once — copy now):
          </p>
          <div className="flex items-center gap-2">
            <code className="text-xs break-all flex-1 bg-white p-2 rounded border">
              {newKeyPlaintext}
            </code>
            <button
              type="button"
              onClick={() => handleCopy(newKeyPlaintext)}
              className="p-2 bg-amber-200 rounded hover:bg-amber-300"
              title="Copy"
            >
              <ClipboardDocumentIcon className="w-5 h-5" />
            </button>
          </div>
          <button
            type="button"
            className="mt-2 text-xs text-amber-800 underline"
            onClick={() => setNewKeyPlaintext(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setFormOpen((open) => !open)}
        aria-expanded={formOpen}
        className="mb-4 inline-flex items-center gap-2 px-4 py-2 bg-green-700 text-white rounded text-sm hover:bg-green-800"
      >
        New MCP key
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-300 ${formOpen ? 'rotate-180' : ''}`}
          strokeWidth={2}
          aria-hidden="true"
        />
      </button>

      <div
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
          formOpen
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0 pointer-events-none'
        }`}
        aria-hidden={!formOpen}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="mb-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Key name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full max-w-xl border rounded px-3 py-2 text-sm bg-gray-50"
                placeholder="e.g. Personal assistant"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Scopes</label>
              <ScopeChips
                availableScopes={availableScopes}
                selectedScopes={selectedScopes}
                onToggle={toggleIn(setSelectedScopes)}
              />
            </div>
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className="px-4 py-2 bg-module-accounts text-white rounded hover:bg-module-accounts/90 disabled:opacity-50 text-sm"
            >
              {creating ? 'Creating…' : 'Create MCP key'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-sm font-medium text-gray-700">Your keys</span>
        {revokedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowRevoked((v) => !v)}
            className="inline-flex items-center gap-1.5 px-1.5 py-1 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
            title={showRevoked ? 'Hide revoked keys' : 'Show revoked keys'}
            aria-label={showRevoked ? 'Hide revoked keys' : 'Show revoked keys'}
          >
            {showRevoked ? (
              <EyeIcon className="w-5 h-5" />
            ) : (
              <EyeSlashIcon className="w-5 h-5" />
            )}
            <span>{showRevoked ? 'showing revoked' : 'hiding revoked'}</span>
          </button>
        )}
      </div>

      <ul className="space-y-2">
        {visibleKeys.length === 0 && (
          <li className="text-sm text-gray-500">
            {keys.length === 0
              ? 'No keys yet.'
              : 'No active keys. Toggle to show revoked.'}
          </li>
        )}
        {visibleKeys.map((k) => {
          const isEditing = editingId === k.id;
          return (
            <li
              key={k.id}
              className="border rounded-lg p-3 bg-gray-50"
            >
              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Key name
                    </label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full max-w-md border rounded px-3 py-2 text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scopes
                    </label>
                    <ScopeChips
                      availableScopes={availableScopes}
                      selectedScopes={editScopes}
                      onToggle={toggleIn(setEditScopes)}
                    />
                  </div>
                  <div className="text-xs text-gray-500 font-mono">{k.key_prefix}…</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      disabled={savingEdit}
                      className="px-3 py-1.5 bg-module-accounts text-white rounded hover:bg-module-accounts/90 disabled:opacity-50 text-sm"
                    >
                      {savingEdit ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={savingEdit}
                      className="px-3 py-1.5 border rounded text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm">
                      {k.name}{' '}
                      {!k.is_active && (
                        <span className="text-red-600 text-xs font-normal">(revoked)</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">{k.key_prefix}…</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {(k.scopes || []).join(', ') || 'no scopes'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      created {k.created_at ? new Date(k.created_at).toLocaleString() : '—'}
                      {k.last_used_at
                        ? ` · last used ${new Date(k.last_used_at).toLocaleString()}`
                        : ''}
                    </div>
                  </div>
                  {k.is_active && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(k)}
                        className="p-2 text-gray-600 hover:bg-gray-200 rounded"
                        title="Edit name / scopes"
                      >
                        <PencilSquareIcon className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRevoke(k.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Revoke"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default McpKeysSection;
